import { forwardRef, memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { MoreHorizontal } from 'react-feather';
import styled from '@emotion/styled';
import { useFloating, flip, shift } from '@floating-ui/react';
import DeleteIcon from '@mui/icons-material/Delete';
import DoneIcon from '@mui/icons-material/Done';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LinkIcon from '@mui/icons-material/Link';
import PushPinIcon from '@mui/icons-material/PushPin';
import ReplyIcon from '@mui/icons-material/Reply';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { ReportFormat } from '@trinity/chat';
import { useChatMessageCapabilities } from '@trinity/chat-components/components/Chat/hooks/useChatMessageCapabilities';
import { useIsFullPageChat } from '@trinity/chat-components/components/Chat/hooks/useIsFullPageChat';
import { PopperMenu } from '@trinity/chat-components/components/Popper';
import { Portal } from '@trinity/chat-components/components/Portal';
import { useZIndexMiddleware } from '@trinity/chat-components/components/presentation/popper-modifiers';
import { useGroupFeatures } from '@trinity/chat-components/containers/Chat/hooks.js';
import { useCopyToClipboard } from '@trinity/chat-components/hooks/useCopyToClipboard';
import { buildBrandInsightsChatLink } from '@trinity/chat-components/utils';
import { ChatHistoryItemContext } from './context';
import { useToggleChatBookmark } from './hooks';
import { useDownloadChatInstance } from './hooks/useDownloadChatInstance';
import { useDeleteChatModal } from './Modal.DeleteChat';
import { useRenameChatModal } from './Modal.RenameChat';
import { useShareChatModal } from './Modal.ShareChat';

type Props = {
  open: boolean;
  refElement: HTMLDivElement;
  setOpen: (open: boolean) => void;
};

type MenuAnchorProps = {
  className?: string;
  open: boolean;
  onClick: (e: React.MouseEvent) => void;
};

export const ChatActionsAnchor = memo(forwardRef<HTMLDivElement, MenuAnchorProps>(({ className, open, onClick }, ref) => (
  <div
    className={className}
    ref={ref}
    onClick={onClick}>
    <Anchor
      open={open} />
  </div>
)));

export const ChatActionsMenu = ({ open, refElement, setOpen }: Props) => {
  const { item } = useContext(ChatHistoryItemContext);

  const zIndexMiddleware = useZIndexMiddleware();
  const { refs, floatingStyles } = useFloating({
    middleware: [
      flip({ fallbackPlacements: ['top-start', 'top-end', 'bottom-end'] }),
      shift({ padding: 8 }),
      zIndexMiddleware,
    ],
    placement: 'bottom-start',
  });
  const features = useGroupFeatures();
  const disableActions = features.deleteChatsOnSessionEnd;
  const { canCopyMessages, canShareChats, canExportChats } = useChatMessageCapabilities();

  useEffect(() => refs.setReference(refElement), [refElement, refs]);

  const [toggleRenameModal, RenameModal] = useRenameChatModal();
  const [toggleDeleteModal, DeleteModal] = useDeleteChatModal();
  const [toggleShareModal, ShareModal] = useShareChatModal();
  const { download } = useDownloadChatInstance({
    chatInstanceId: item.id,
    name: item.name,
  });
  const copy = useCopyToClipboard();
  const [copied, setCopied] = useIconFlipper(2500);
  const toggleBookmarkMutation = useToggleChatBookmark({ chatInstanceId: item.id });

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleRenameClick = useCallback((_e: React.MouseEvent) => {
    toggleRenameModal();
    setOpen(false);
  }, [setOpen, toggleRenameModal]);

  const handleDeleteClick = useCallback((_e: React.MouseEvent) => {
    toggleDeleteModal();
    setOpen(false);
  }, [setOpen, toggleDeleteModal]);

  const handleShareClick = useCallback((_e: React.MouseEvent) => {
    toggleShareModal();
    setOpen(false);
  }, [setOpen, toggleShareModal]);

  const handleDownloadClick = useCallback((_e: React.MouseEvent) => {
    download({
      title: `Generating Export`,
      name: item.name,
      extension: ReportFormat.Word,
    });
    setOpen(false);
  }, [download, item.name, setOpen]);

  const isStandaloneChat = useIsFullPageChat();

  const handleCopyClick = useCallback((_e: React.MouseEvent) => {
    copy(buildBrandInsightsChatLink(({
      chatIdentifier: item.identifier,
      isStandaloneChat,
    })));
    setCopied();
  }, [copy, isStandaloneChat, item.identifier, setCopied]);

  const handleBookmarkClick = useCallback(() => {
    toggleBookmarkMutation.mutate(!item.bookmarked);
    setOpen(false);
  }, [item.bookmarked, setOpen, toggleBookmarkMutation]);

  return (
    <>
      {open && (
        <Portal>
          <ClickAwayListener
            onClickAway={handleClose}>
            <div
              ref={refs.setFloating}
              style={floatingStyles}>
              <PopperMenu>
                <StyledPopperMenuItem disabled={disableActions} onClick={disableActions ? undefined : handleBookmarkClick}>
                  <StyledPushPinIcon /> {item.bookmarked ? `Unpin` : `Pin`}
                </StyledPopperMenuItem>
                {!item.shared && (
                  <StyledPopperMenuItem disabled={disableActions} onClick={disableActions ? undefined : handleRenameClick}>
                    <StyledRenameIcon /> Rename
                  </StyledPopperMenuItem>
                )}
                {canExportChats && (
                  <StyledPopperMenuItem onClick={handleDownloadClick}>
                    <StyledDownloadIcon /> Download
                  </StyledPopperMenuItem>
                )}
                {canShareChats && !item.shared && (
                  <StyledPopperMenuItem disabled={disableActions} onClick={disableActions ? undefined : handleShareClick}>
                    <StyledShareIcon /> Share
                  </StyledPopperMenuItem>
                )}
                {canCopyMessages && (
                  <StyledPopperMenuItem disabled={disableActions} onClick={disableActions ? undefined : handleCopyClick}>
                    {copied
                      ? <StyledDoneIcon />
                      : <StyledLinkIcon />}
                    Copy link
                  </StyledPopperMenuItem>
                )}
                {!item.shared && (
                  <StyledPopperMenuItem disabled={disableActions} onClick={disableActions ? undefined : handleDeleteClick}>
                    <StyledDeleteIcon /> Delete
                  </StyledPopperMenuItem>
                )}
              </PopperMenu>
            </div>
          </ClickAwayListener>
        </Portal>
      )}
      <ShareModal chatInstance={item} />
      <DeleteModal chatInstance={item} />
      <RenameModal chatInstance={item} />
    </>
  );
};

type AnchorProps = {
  open: boolean;
};

const Anchor = memo(forwardRef<HTMLDivElement, AnchorProps>(({ open }, ref) => {
  return (
    <AnchorRoot open={open} ref={ref}>
      <StyledMoreHorizontal />
    </AnchorRoot>
  );
}));

const AnchorRoot = styled('div', {
  shouldForwardProp: prop => prop !== 'open',
})<AnchorProps>(({ theme, open }) => ({
  cursor: 'pointer',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  color: open ? theme.palette.black.light1 : undefined,
  backgroundColor: open ? theme.palette.gray.light2 : undefined,

  ':hover': {
    color: theme.palette.black.light1,
    backgroundColor: theme.palette.gray.light2,
  },
}));

const StyledMoreHorizontal = styled(MoreHorizontal)({
  width: 25,
  height: 25,
  padding: 5,
  boxSizing: 'border-box',
});

const iconMargin = 8;

const StyledRenameIcon = styled(DriveFileRenameOutlineIcon)(() => ({
  marginRight: iconMargin,
}));

const StyledDeleteIcon = styled(DeleteIcon)(() => ({
  marginRight: iconMargin,
}));

const StyledDownloadIcon = styled(FileDownloadIcon)(() => ({
  marginRight: iconMargin,
}));

const StyledLinkIcon = styled(LinkIcon)(() => ({
  marginRight: iconMargin,
}));

const StyledShareIcon = styled(ReplyIcon)(() => ({
  transform: 'scaleX(-1)',
  marginRight: iconMargin,
}));

const StyledDoneIcon = styled(DoneIcon)(({ theme }) => ({
  color: theme.palette.green.main,
  marginRight: iconMargin,
}));

const StyledPushPinIcon = styled(PushPinIcon)(({ theme }) => ({
  color: theme.palette.black.light1,
  marginRight: iconMargin,
}));

type PopperMenuItemProps = {
  disabled?: boolean;
};

const StyledPopperMenuItem = styled.div<PopperMenuItemProps>(({ disabled, theme }) => {
  const color = disabled ? theme.palette.gray.main : theme.palette.black.light1;
  return {
    display: 'flex',
    fontSize: 14,
    alignItems: 'center',
    cursor: disabled ? 'default' : 'pointer',
    color,
    height: 40,
    boxSizing: 'border-box',
    padding: '0 20px',

    [String(StyledRenameIcon)]: {
      color,
    },
    [String(StyledDeleteIcon)]: {
      color,
    },
    [String(StyledDownloadIcon)]: {
      color,
    },
    [String(StyledLinkIcon)]: {
      color,
    },
    [String(StyledShareIcon)]: {
      color,
    },
    [String(StyledPushPinIcon)]: {
      color,
    },

    '&:hover': disabled ? undefined : {
      color: theme.palette.sentiment.primary.main,

      [String(StyledRenameIcon)]: {
        color: theme.palette.sentiment.primary.main,
      },
      [String(StyledDeleteIcon)]: {
        color: theme.palette.sentiment.primary.main,
      },
      [String(StyledDownloadIcon)]: {
        color: theme.palette.sentiment.primary.main,
      },
      [String(StyledLinkIcon)]: {
        color: theme.palette.sentiment.primary.main,
      },
      [String(StyledShareIcon)]: {
        color: theme.palette.sentiment.primary.main,
      },
      [String(StyledPushPinIcon)]: {
        color: theme.palette.sentiment.primary.main,
      },
    },
  };
});
export function useIconFlipper(timeoutMs: number) {
  const [flipped, setFlipped] = useState(false);
  const timer = useRef<NodeJS.Timeout>(undefined);

  const handleSetFlipped = useCallback(() => {
    setFlipped(true);

    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      setFlipped(false);
    }, timeoutMs);
  }, [timeoutMs]);

  return [flipped, handleSetFlipped] as const;
}